import { User, UserCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ProfilePhotoGender } from "@/config/profilePhotoPresets";

interface ProfilePhotoGenderPickerProps {
  value: ProfilePhotoGender | null;
  onChange: (gender: ProfilePhotoGender) => void;
}

export function ProfilePhotoGenderPicker({
  value,
  onChange,
}: ProfilePhotoGenderPickerProps) {
  const { t } = useLanguage();

  const items: Array<{ id: ProfilePhotoGender; label: string; Icon: typeof User }> = [
    { id: "male", label: t.profilePhoto.genderMale, Icon: User },
    { id: "female", label: t.profilePhoto.genderFemale, Icon: UserCircle2 },
  ];

  return (
    <div>
      <h3 className="text-base md:text-lg font-semibold text-foreground mb-3">
        {t.profilePhoto.genderQuestion}
      </h3>
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        {items.map(({ id, label, Icon }) => {
          const isActive = value === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className={cn(
                "flex flex-col items-center justify-center gap-2 rounded-2xl border-2 p-6 transition-all duration-200",
                isActive
                  ? "border-brand bg-brand/5 shadow-sm ring-2 ring-brand/30"
                  : "border-border bg-card hover:border-brand/40 hover:bg-card",
              )}
            >
              <Icon
                className={cn(
                  "w-8 h-8",
                  isActive ? "text-brand" : "text-muted-foreground",
                )}
              />
              <span
                className={cn(
                  "text-sm md:text-base font-medium",
                  isActive ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
